
/*
 * Symboler till kartan
 * Dokumentation: https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-Symbol.html
 */
import CIMSymbol from "@arcgis/core/symbols/CIMSymbol";
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol";
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol";
import SimpleMarkerSymbol from "@arcgis/core/symbols/SimpleMarkerSymbol";
import TextSymbol from "@arcgis/core/symbols/TextSymbol";
import { Colors, EsriColor, EsriFillStyle, EsriLineStyles } from "./symbol.constants";
export class Symbols {

    static readonly Styles = {
        editing: Symbols.Point(Colors.cyan, Colors.black, 10, 1),
        editingPolygon: Symbols.Filled(Colors.transparent, Colors.cyan, 1, "solid", "none"),
        editingLine: Symbols.Line(Colors.black, "solid", 2),

        default: Symbols.Line(Colors.black, "solid", 0.5),
        defaultPoint: Symbols.Point(Colors.darkgrey, Colors.black, 10, 1),
        selected: Symbols.Line(Colors.selected, "solid", 2),
        selectedPolygon: Symbols.Filled(Colors.transparent, Colors.selected, 2, "solid", "none"),
        extentDetail: Symbols.Line([
            255,
            0,
            0,
            1
        ]),
        extentOverview1: Symbols.Line([
            0,
            255,
            255,
            1
        ], "dash"),
        extentOverview2: Symbols.Line([
            255,
            0,
            255,
            1
        ], "dash"),
        atgardYta: Symbols.Line([
            169,
            0,
            230,
            1
        ]),
        skogsvardMarkberedningYta: Symbols.DoubleLine([
            138,
            72,
            12,
            255
        ], [
            255,
            255,
            255,
            255
        ], 3),
        skogsvardPlanteringYta: Symbols.DoubleLine([
            17,
            145,
            107,
            255
        ], [
            255,
            255,
            255,
            255
        ], 3),
        skogsvardRojningYta: Symbols.Line([
            230,
            94,
            0,
            1
        ], "solid" , 3),
        skogsvardInventeringYta: Symbols.Line([
            58,
            79,
            98,
            255
        ], "solid" , 3),
        skogsvardOvrigYta: Symbols.DoubleOuterLine([
            173,
            219,
            255,
            255
        ], [
            0,
            0,
            0,
            255
        ], 3), //Svart ram
        skogsvardTradslag: Symbols.Text(20, Colors.black, Colors.grey, "center", 0, 0),

        hansynLinje: Symbols.Line("#38A800", "short-dash-dot"),

        drivningslinje: Symbols.Line("#A0A09F"),
        vattendrag: Symbols.Line("#006FFF", "short-dot"),
        kraftledning: Symbols.Line("#E64D00"),
        bilvag: Symbols.Line("#37A800"),

        traktorvagKlassD: Symbols.Line("#CDCDCD", "short-dash"),
        basvag: Symbols.Line("#E60000", "short-dash"),
        vinterbilbasvag: Symbols.Line("#CDCDCD"),
        overfartDrivningPoint: Symbols.Point(Colors.red, Colors.black, 10, 1),
        overfartDrivning: Symbols.Text(14, Colors.black, Colors.grey, "left", 10, -3),
        hansynYta: Symbols.Filled([
            56,
            168,
            0,
            0.5
        ], "#A3FF74", 1),
        anvisningYta: Symbols.Filled([
            1,
            255,
            255,
            0.25
        ], "#01ffff", 1),
        allmanlinje: Symbols.Line(Colors.darkyellow, "short-dash"),
        allmanlinjeMindreVattendrag: Symbols.Line(Colors.vattendrag, "solid"),
        allmanlinjeBackA: Symbols.Line(Colors.vattendrag, "long-dash"),
        allmanlinjeJordkabel: Symbols.Line(Colors.jordkabel, "short-dash"),
        allmanlinjeStup: Symbols.Line(Colors.stup, "short-dash-dot-dot"),
        allmanlinjeSkyddsdike: Symbols.Line(Colors.skyddsdike, "short-dash"),
        allmanlinjeStig: Symbols.Line(Colors.stig, "short-dot"),
        allmanlinjeDike: Symbols.Line(Colors.dike, "short-dash"),

        allmanpunkt: Symbols.Point(Colors.yellow, "#000000"),

        lassoline1: Symbols.Line(Colors.darkgrey, "solid", 2),
        lassoline2: Symbols.Line(Colors.selection, "dash", 1),
        lassofill: Symbols.Filled([
            255,
            255,
            128,
            0.8
        ], [
            255,
            255,
            128,
            0.8
        ], 2, "solid", "diagonal-cross"),

        // Icons
        avlagg: Symbols.ImageIcon("/core/assets/img/karta/avlagg.png", 25),
        kojplats: Symbols.ImageIcon("/core/assets/img/karta/kojplats.png", 25),
        larmpunkt: Symbols.ImageIcon("/core/assets/img/karta/larm.png", 25),
        overfart: Symbols.ImageIcon("/core/assets/img/karta/overfart.png", 25),
        hansynPunkt: Symbols.ImageIcon("/core/assets/img/karta/hansynPunkt.png", 25),
        allmanpunktBerg: Symbols.ImageIcon("/core/assets/img/karta/berg.png", 25),
        allmanpunktSankmark: Symbols.ImageIcon("/core/assets/img/karta/sankmark.png", 25),
        allmanpunktBlockmark: Symbols.ImageIcon("/core/assets/img/karta/blockmark.png", 25),
        allmanpunktBrantStup: Symbols.ImageIcon("/core/assets/img/karta/brantStup.png", 25),

        // SVG-Path hämtad från https://fontawesome.com/icons/map-marker-alt?style=solid

        positionMarker: Symbols.SvgPathIcon(Colors.positionPointer, 0, 11, 25, "M172.268 501.67C26.97 291.031 0 269.413 0 192 0 85.961 85.961 0 192 0s192 85.961 192 192c0 77.413-26.97 99.031-172.268 309.67-9.535 13.774-29.93 13.773-39.464 0zM192 272c44.183 0 80-35.817 80-80s-35.817-80-80-80-80 35.817-80 80 35.817 80 80 80z"),

        measureLine: Symbols.Line(Colors.darkgrey, "short-dash-dot"),

        // Events:
        defaultSelected: { outline: {
            type: "simple-line",
            color: Colors.selected,
            width: 1,
            style: "solid"
        } }
    };

    private static ImageIcon(imageUrl: string, size = 16) {
        // OBS: Går ej att typa denna till PictureSymbolMarker då detta objekt hanteras felaktigt av ArcGis JS.
        // Vi får ett Runtime Error djupt nere i Esri ArcGis om vi gör det. Det kompilerar alltså finfint vilket är problematiskt i sig.
        // Uncaught TypeError: [accessor] cannot assign to read-only property 'source' of esri.symbols.PictureMarkerSymbol
        return {
            type: "picture-marker",
            url: imageUrl,
            height: `${size}px`,
            width: `${size}px`
        };
    }

    private static Point(
        color: EsriColor,
        lineColor: EsriColor,
        size = 16,
        lineWidth = 2,
        lineStyle: EsriLineStyles = "solid"): SimpleMarkerSymbol {

        return new SimpleMarkerSymbol({
            color: color,
            size: size,
            outline: {
                width: lineWidth,
                color: lineColor,
                style: lineStyle
            }
        });
    }

    private static SvgPathIcon(
        color: EsriColor,
        xoffset: string | number,
        yoffset: string | number,
        size: string | number,
        path: string): SimpleMarkerSymbol {

        return new SimpleMarkerSymbol({
            path: path,
            size: size,
            yoffset: yoffset,
            xoffset: xoffset,
            color: color
        });
    }

    private static Text(fontSize: number,
        color: EsriColor,
        haloColor: EsriColor,
        alignment: "center" | "right" | "left",
        xoffset: number,
        yoffset: number): TextSymbol {

        return new TextSymbol({
            text: "[insert text]",
            color: color,
            haloColor: haloColor,
            haloSize: "2px",
            xoffset: xoffset ?? fontSize * 0.7,
            yoffset: yoffset ?? fontSize * 0.35,
            horizontalAlignment: alignment,
            font: {
                size: fontSize,
                weight: "bold",
                family: "serif"
            }
        });
    }

    private static Filled(
        fillColor: EsriColor,
        lineColor: EsriColor,
        lineWidth = 2,
        lineStyle: EsriLineStyles = "solid",
        fillStyle: EsriFillStyle = "solid"): SimpleFillSymbol {

        return new SimpleFillSymbol({
            style: fillStyle,
            color: fillColor,
            outline: {
                width: lineWidth,
                color: lineColor,
                style: lineStyle
            }
        });
    }

    private static DoubleOuterLine(innerColorRGBA: [number, number, number, number], outerColorRGBA: [number, number, number, number], lineWidth: number): CIMSymbol {
        // Ritar två linjer
        // En linje direkt på origo som är 0,0 bredare så den syns på bägge sidor om den som kommer över.
        return new CIMSymbol({
            data: {
                type: "CIMSymbolReference",
                symbol: {
                    type: "CIMLineSymbol",
                    symbolLayers: [
                        {
                            type: "CIMSolidStroke",
                            enable: true,
                            effects: [
                                {
                                    type: "CIMGeometricEffectOffset",
                                    method: "Mitered",
                                    offset: 0,
                                    option: "Fast"
                                }
                            ],
                            capStyle: "Butt",
                            joinStyle: "Miter",
                            width: lineWidth,
                            color: innerColorRGBA
                        },
                        {
                            type: "CIMSolidStroke",
                            enable: true,
                            capStyle: "Butt",
                            joinStyle: "Miter",
                            width: (lineWidth*1.4),
                            color: outerColorRGBA
                        }
                    ]
                }
            }
        });
    }

    private static DoubleLine(innerColorRGBA: [number, number, number, number], outerColorRGBA: [number, number, number, number], lineWidth: number): CIMSymbol {
        // Ritar två linjer
        // En linje direkt på origo som där dubbelt så bred som önskat
        // En linje med offset som ritar över halva linjen
        // Resultatet är två linjer med exakt samma width.
        // 0.5-hanteringen har enbart att göra med att ArcGis inte ritar ut linjerna perfekt så
        // så den underliggande linjen visas utanför ibland. Denna fix gör så att det finns mer marginal
        // för ArcGis att jobba med.
        return new CIMSymbol({
            data: {
                type: "CIMSymbolReference",
                symbol: {
                    type: "CIMLineSymbol",
                    symbolLayers: [
                        {
                            type: "CIMSolidStroke",
                            enable: true,
                            effects: [
                                {
                                    type: "CIMGeometricEffectOffset",
                                    method: "Mitered",
                                    offset: (lineWidth/2),
                                    option: "Fast"
                                }
                            ],
                            capStyle: "Butt",
                            joinStyle: "Miter",
                            width: lineWidth+0.5,
                            color: outerColorRGBA
                        },
                        {
                            type: "CIMSolidStroke",
                            enable: true,
                            capStyle: "Butt",
                            joinStyle: "Miter",
                            width: (lineWidth*2)-0.5,
                            color: innerColorRGBA
                        }
                    ]
                }
            }
        });
    }

    private static Line(color: EsriColor, style: EsriLineStyles = "solid", size = 1): SimpleLineSymbol {
        return new SimpleLineSymbol({
            color: color,
            width: size,
            style: style,
            cap: "round",
            join: "round"
        });
    }

}
